import { Component, inject } from '@angular/core';

import { MatCardModule } from "@angular/material/card";
import { MatListModule } from "@angular/material/list";
import { MatDividerModule } from "@angular/material/divider";
import { MatChipsModule } from "@angular/material/chips";
import { WorkflowBuilderService } from "../services/workflow-builder.service";
import { IWorkflow } from "../../../interfaces/i-workflow";
import { ProfileService } from "../../../../../services/profile.service";
import { AgentService } from "../../../../agent/services/agent.service";
import { IInstructions } from "../../../interfaces/i-instructions";
import { SpinnerService } from "../../../../../shared/spinner/spinner.service";
import { ApiService } from "../../../../../services/api.service";
import { AlertService } from "../../../../../shared/alert/alert.service";
import { AlertEnum } from "../../../../../shared/alert/alert.enum";
import { SensitivityLabelService } from "../../../../../services/sensitivity-label.service";

/**
 * WorkflowSummaryComponent provides a summary view of the workflow being created or edited.
 * It ensures workflow ownership defaults are set and displays workflow and instruction details for review.
 */
@Component({
  selector: 'workflow-summary',
  imports: [
    MatCardModule,
    MatListModule,
    MatDividerModule,
    MatChipsModule
],
  templateUrl: './workflow-summary.component.html',
  styleUrl: './workflow-summary.component.scss'
})
export class WorkflowSummaryComponent {
  workflow: Partial<IWorkflow>;
  agentService = inject(AgentService);

  #sensitivityLabelService = inject(SensitivityLabelService);
  /** Whether the sensitivity-label feature is enabled; label rows are hidden when disabled. */
  readonly slEnabled = this.#sensitivityLabelService.enabled;

  #profileService = inject(ProfileService);
  #workflowBuilderService = inject(WorkflowBuilderService);
  #spinnerService = inject(SpinnerService);
  #apiService = inject(ApiService);
  #alertService = inject(AlertService);

  constructor() {
    // There is a chance that the user has filled out steps 1 & 2, but not 3 & 4 and then navigated to the summary.
    // This is possible because steps 3 & 4 have default values.  The problem is, if the process bypasses step 3,
    // the default value is not set in the workflow, only in the form.  We need to ensure that the workflow has the owner's
    // default value set which is the current user.
    // Check if the workflow has an owner set, if not, set it to the current user.
    // We also need to do the same for the isEnabled and isUniversal properties.
    // These properties could be undefined if the user has not set them in the form.
    const currentWorkflow = this.#workflowBuilderService.workflow();

    // Ensure isEnabled and isUniversal are booleans and owners is a non-empty array; if not, set defaults
    if (
      typeof currentWorkflow.details?.isEnabled !== 'boolean' ||
      typeof currentWorkflow.details?.isUniversal !== 'boolean' ||
      !currentWorkflow.details?.owners ||
      currentWorkflow.details.owners.length === 0
    ) {
      this.#workflowBuilderService.setDefaultSecurityAndAdminValues();
    }

    this.workflow = this.#workflowBuilderService.workflow();

    // Just like in the aforementioned case where the user skips steps 3 & 4, they can also skip step 2 if they are
    // editing or cloning a workflow.  In this case, we need to ensure that the this.#workflowBuilderService.instructions()
    // has the instructions set.  If the instructions() is empty, we need to call the API and fetch the instructions then
    // set them in instructions().
    if (this.#workflowBuilderService.instructions().length === 0) {
      this.#spinnerService.show();
      this.#apiService.getInstructions().subscribe({
        next: (instructions: IInstructions[]) => {
          // Get instruction IDs from workflow
          const workflowInstructionIds = (this.#workflowBuilderService.workflow().details!.instructions || []);
          // Map IDs to instruction objects, preserving order
          const selectedInstructions = workflowInstructionIds
            .map(id => instructions.find(instr => instr.id === id))
            .filter(Boolean) as IInstructions[];
          // Set instructions in the service
          this.#workflowBuilderService.instructions.set(selectedInstructions);
          this.#spinnerService.hide();
        },
        error: (error: any) => {
          this.#spinnerService.hide();
          this.#alertService.showAlert({
            message: `Failed to load instructions for the workflow.`,
            type: AlertEnum.error,
          });
        }
      });
    }
  }

  /**
   * Returns the instruction object for the given instruction id from the workflow builder service.
   * @param id The instruction id to look up.
   * @returns The IInstructions object if found, otherwise undefined.
   */
  getInstruction(id: string): IInstructions | undefined {
    return this.#workflowBuilderService.instructions().find(instr => instr.id === id);
  }
}
